import request from '../../utils/request';
import { submitFile } from '../formSubmitLogic';

/**
 * The engine's `onSubmit` for Vulcan — the I/O half of the old `vulcanSubmitPayload`
 * (formSubmitLogic.js:265), which the payload adapter deliberately left out
 * (adapters SHAPE only, no side effects). This ALSO fixes the pre-existing
 * constitution §4.1 violation: the original issued the order create/update via a
 * raw `axios.put`/`axios.post` (bypassing the app's centralized `request.js`);
 * this routes it through `request` like every other submit path.
 *
 * The engine passes the RESOLVED, already-shaped payload (completedAt / status /
 * typeOfOrder / step / isThisVulcanOrder set by `createVulcanPayloadAdapter`), so
 * this only decides create-vs-update and performs the I/O:
 *   - existing order (has id) → `PUT /orders/:id`, plus the file-upload side
 *     effect on the installation step (step 2), unchanged (`submitFile` already
 *     uses `request.put`);
 *   - new order → `POST /orders`.
 *
 * `currentStep` (1-based) is the fallback for the file-upload gate when the
 * payload doesn't carry `step` (the adapter only sets `step` on new orders).
 *
 * @param {{ orderData?: Object, currentStep?: number }} [appCtx]
 * @returns {(payload: Object) => Promise<*>}
 */
export function createVulcanSubmitHandler({
  orderData = {},
  currentStep
} = {}) {
  return async payload => {
    const id = payload.id || orderData.id;

    if (id) {
      const stepForFiles = payload.step != null ? payload.step : currentStep;
      const { files, attachmentFile } = payload;
      if (files && [2].includes(stepForFiles)) {
        await submitFile(payload, files, id);
      }
      if (attachmentFile && [2].includes(stepForFiles)) {
        await submitFile(payload, attachmentFile, id);
      }
      return request.put(`/orders/${id}`, payload);
    }

    return request.post('/orders', payload);
  };
}

export default createVulcanSubmitHandler;
